import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Save, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
});

export default function AdminFAQs() {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [formData, setFormData] = useState({
    category_id: "",
    question: "",
    answer: "",
    order: 0,
    is_active: true,
  });

  const fetchData = async () => {
    try {
      const [faqsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/faqs`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categories`),
      ]);
      setFaqs(faqsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        category_id: formData.category_id || null,
      };
      
      if (editingFaq) {
        await axios.put(`${API}/faqs/${editingFaq.id}`, submitData, {
          headers: getAuthHeaders(),
        });
        toast.success("SSS güncellendi");
      } else {
        await axios.post(`${API}/faqs`, submitData, {
          headers: getAuthHeaders(),
        });
        toast.success("SSS eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu SSS'yi silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/faqs/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("SSS silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      category_id: faq.category_id || "",
      question: faq.question,
      answer: faq.answer,
      order: faq.order,
      is_active: faq.is_active,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingFaq(null);
    setFormData({
      category_id: "",
      question: "",
      answer: "",
      order: 0,
      is_active: true,
    });
  };

  const filteredFaqs = filterCategory === "all" 
    ? faqs 
    : faqs.filter(f => f.category_id === filterCategory);

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || "Genel";

  return (
    <AdminLayout title="Sıkça Sorulan Sorular (SSS)">
      <div className="space-y-6" data-testid="admin-faqs">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Kategori Filtrele" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-electric-500 hover:bg-electric-600"
            data-testid="add-faq-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni SSS
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredFaqs.map((faq) => (
              <Card key={faq.id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <HelpCircle className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                            {getCategoryName(faq.category_id)}
                          </span>
                          <span className="text-xs text-slate-400">Sıra: {faq.order}</span>
                        </div>
                        <h3 className="font-semibold text-navy-900 mb-2">{faq.question}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2">{faq.answer}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-1 text-xs rounded-full ${faq.is_active ? "bg-signal-100 text-signal-700" : "bg-slate-100 text-slate-500"}`}>
                        {faq.is_active ? "Aktif" : "Pasif"}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(faq)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(faq.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {filteredFaqs.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                Bu kategoride SSS bulunmuyor.
              </div>
            )}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingFaq ? "SSS Düzenle" : "Yeni SSS"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kategori</Label>
                  <Select 
                    value={formData.category_id} 
                    onValueChange={(v) => setFormData({ ...formData, category_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kategori seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Genel</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sıra</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Soru (Almanca) *</Label>
                <Input
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  placeholder="Wie wechsle ich meinen Stromanbieter?"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Cevap (Almanca) *</Label>
                <Textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  placeholder="Der Wechsel ist einfach: Wählen Sie einen neuen Tarif..."
                  rows={5}
                  required
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Aktif</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" className="bg-electric-500 hover:bg-electric-600">
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
